import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

const categoryThumbnails: Record<number, string> = {
  911: "https://readdy.ai/api/search-image?query=Single%20stylish%20white%20leather%20sneaker%20on%20terracotta%20ceramic%20surface%20soft%20natural%20lighting%20clean%20product%20photography%20warm%20tones%20minimalist%20style%20high%20detail&width=100&height=100&seq=thumb-shoes-v2&orientation=squarish",
  912: "https://readdy.ai/api/search-image?query=Cozy%20beige%20oversized%20hoodie%20folded%20on%20soft%20linen%20texture%20gentle%20morning%20light%20minimalist%20product%20photography%20warm%20neutral%20tones%20high%20detail%20clean%20aesthetic&width=100&height=100&seq=thumb-hoodies-v2&orientation=squarish",
  913: "https://readdy.ai/api/search-image?query=White%20cotton%20t%20shirt%20neatly%20folded%20on%20pale%20sage%20green%20surface%20soft%20daylight%20minimalist%20product%20photography%20warm%20background%20high%20detail%20clean%20composition&width=100&height=100&seq=thumb-tshirts-v2&orientation=squarish",
  914: "https://readdy.ai/api/search-image?query=Olive%20green%20bomber%20jacket%20laid%20flat%20on%20warm%20sand%20textured%20surface%20soft%20diffused%20lighting%20minimalist%20fashion%20photography%20neutral%20background%20high%20detail%20editorial%20style&width=100&height=100&seq=thumb-jackets-v2&orientation=squarish",
  915: "https://readdy.ai/api/search-image?query=Well%20tailored%20beige%20trousers%20on%20ivory%20textured%20surface%20soft%20natural%20lighting%20minimalist%20product%20photography%20warm%20neutral%20background%20high%20detail%20clean%20composition&width=100&height=100&seq=thumb-pants-v2&orientation=squarish",
  916: "https://readdy.ai/api/search-image?query=Neutral%20toned%20baseball%20cap%20on%20light%20clay%20textured%20surface%20soft%20daylight%20minimalist%20product%20photography%20warm%20background%20high%20detail%20clean%20aesthetic&width=100&height=100&seq=thumb-headwear-v2&orientation=squarish",
  917: "https://readdy.ai/api/search-image?query=Matching%20hoodie%20and%20joggers%20set%20on%20pale%20blush%20surface%20soft%20natural%20lighting%20minimalist%20fashion%20photography%20warm%20neutral%20background%20high%20detail%20editorial%20style&width=100&height=100&seq=thumb-sets-v2&orientation=squarish",
  918: "https://readdy.ai/api/search-image?query=Soft%20cotton%20underwear%20folded%20on%20light%20gray%20textured%20surface%20gentle%20natural%20lighting%20minimalist%20product%20photography%20warm%20neutral%20background%20high%20detail%20clean%20composition&width=100&height=100&seq=thumb-underwear-v2&orientation=squarish",
  919: "https://readdy.ai/api/search-image?query=Sports%20jersey%20on%20warm%20oat%20colored%20surface%20soft%20afternoon%20lighting%20minimalist%20product%20photography%20neutral%20background%20high%20detail%20dynamic%20composition%20clean%20aesthetic&width=100&height=100&seq=thumb-jersey-v2&orientation=squarish",
  920: "https://readdy.ai/api/search-image?query=Elegant%20watch%20and%20bracelet%20on%20dark%20sand%20textured%20surface%20soft%20diffused%20lighting%20minimalist%20luxury%20photography%20warm%20neutral%20background%20high%20detail%20editorial%20style&width=100&height=100&seq=thumb-accessories-v2&orientation=squarish",
  921: "https://readdy.ai/api/search-image?query=Minimalist%20canvas%20backpack%20on%20soft%20stone%20textured%20surface%20natural%20daylight%20clean%20product%20photography%20warm%20neutral%20background%20high%20detail%20editorial%20style&width=100&height=100&seq=thumb-bags-v2&orientation=squarish",
  922: "https://readdy.ai/api/search-image?query=Genuine%20leather%20belt%20coiled%20on%20warm%20taupe%20textured%20surface%20soft%20natural%20lighting%20minimalist%20product%20photography%20neutral%20background%20high%20detail%20clean%20composition&width=100&height=100&seq=thumb-belts-v2&orientation=squarish",
  923: "https://readdy.ai/api/search-image?query=Elegant%20glass%20perfume%20bottle%20on%20pearl%20white%20surface%20with%20soft%20reflections%20diffused%20lighting%20minimalist%20luxury%20photography%20warm%20neutral%20background%20high%20detail%20editorial%20style&width=100&height=100&seq=thumb-perfumes-v2&orientation=squarish",
  924: "https://readdy.ai/api/search-image?query=Miscellaneous%20lifestyle%20products%20on%20warm%20mushroom%20colored%20surface%20soft%20natural%20lighting%20minimalist%20product%20photography%20neutral%20background%20high%20detail%20curated%20composition&width=100&height=100&seq=thumb-other-v2&orientation=squarish",
};

export default function Navbar() {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md border-b border-background-200/60"
            : "bg-background-50/90 backdrop-blur-sm border-b border-background-200/30"
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 flex-shrink-0"
                onClick={closeMobileMenu}
              >
                <span className="font-heading text-xl font-bold text-foreground-900 tracking-tight">
                  kakobuy<span className="text-primary-500">.courses</span>
                </span>
              </Link>

              <div className="hidden md:flex items-center gap-3 ml-6">
                <Link
                  to="/"
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap rounded-lg hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center mr-1.5"></i>
                  Strona główna
                </Link>

                <Link
                  to="/blog/kakobuy-spreadsheet-polska"
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap rounded-lg hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center mr-1.5"></i>
                  Poradnik zakupowy
                </Link>

                <div
                  ref={dropdownRef}
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                    <span>Kategorie</span>
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    ></i>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-xl shadow-lg border border-background-200/70 py-2 max-h-96 overflow-y-auto z-50">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                            <img
                              src={categoryThumbnails[cat.id]}
                              alt={cat.name}
                              title={cat.name + " – Kakobuy Spreadsheet"}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="block truncate">{cat.name}</span>
                          </div>
                          <i className="ri-arrow-right-up-line text-foreground-400 w-3 h-3 flex items-center justify-center flex-shrink-0"></i>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-xs md:text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>kakobuy spreadsheet</span>
                <i className="ri-arrow-right-up-line w-3 h-3 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs md:text-sm font-semibold text-foreground-700 border-2 border-foreground-200 hover:border-foreground-400 hover:text-foreground-900 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>buy it now</span>
                <i className="ri-shopping-cart-line w-3 h-3 flex items-center justify-center"></i>
              </a>
              <button
                className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Menu"
              >
                <i className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl w-5 h-5 flex items-center justify-center`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={closeMobileMenu}></div>
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto">
            <div className="p-4 space-y-6">
              <div>
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-home-4-line text-primary-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">Strona główna</p>
                    <p className="text-xs text-foreground-500">Kakobuy Spreadsheet Polska 2026</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 ml-auto w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>

              <div>
                <Link
                  to="/blog/kakobuy-spreadsheet-polska"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-book-open-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">Poradnik zakupowy</p>
                    <p className="text-xs text-foreground-500">Kompletny przewodnik po arkuszu</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 ml-auto w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>

              <div>
                <h3 className="px-4 mb-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                  Kategorie produktów
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobileMenu}
                      className="flex items-center gap-2.5 p-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={categoryThumbnails[cat.id]}
                          alt={cat.name}
                          title={cat.name + " – Kakobuy Spreadsheet"}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <span className="text-xs font-medium text-foreground-700 truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-background-200 flex flex-col gap-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-primary-500 rounded-xl whitespace-nowrap cursor-pointer"
                >
                  <span>kakobuy spreadsheet</span>
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-700 border-2 border-foreground-200 rounded-xl whitespace-nowrap cursor-pointer"
                >
                  <span>buy it now</span>
                  <i className="ri-shopping-cart-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}